import { useState } from "react";
import { Notify, toast } from "@/components/ui/notify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, getFriendlyErrorMessage } from "@/lib/api-client";
import { isDraftEntityId } from "@/features/lines/canvas/draft-canvas-adapter";

function useReadingCount(sensorId: string | null) {
  return useQuery<{ count: number; since: string }>({
    queryKey: ["sensors", sensorId, "reading-count"],
    queryFn: () => api.get(`/api/v1/sensors/${sensorId}/reading-count`),
    enabled: !!sensorId && !isDraftEntityId(sensorId),
  });
}

function useUnlinkSensor(linkId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/api/v1/sensor-links/${linkId}`),
    meta: { skipErrorToast: true },
    onSuccess: () => {
      // Remove a chave morta ["machine-sheet"]; prefixos cobrem machines/parameters.
      void qc.invalidateQueries({ queryKey: ["sensors"] });
      void qc.invalidateQueries({ queryKey: ["lines"] });
      void qc.invalidateQueries({ queryKey: ["parameters"] });
    },
  });
}

interface UnlinkProps {
  linkId: string | null;
  sensorId: string | null;
  sensorLabel: string;
  open: boolean;
  onClose: () => void;
  onUnlinkOverride?: () => Promise<void>;
}

export function UnlinkSensorDialog({
  linkId,
  sensorId,
  sensorLabel,
  open,
  onClose,
  onUnlinkOverride,
}: UnlinkProps) {
  const readingCountQuery = useReadingCount(open && !onUnlinkOverride ? sensorId : null);
  const { data: counts } = readingCountQuery;
  const unlink = useUnlinkSensor(linkId);
  const [submitting, setSubmitting] = useState(false);
  if (!open || !linkId) return null;

  async function submit() {
    setSubmitting(true);
    try {
      if (onUnlinkOverride) {
        await onUnlinkOverride();
      } else {
        await unlink.mutateAsync();
        toast.success("Sensor desvinculado.");
      }
      onClose();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao desvincular.", { description: msg });
    } finally {
      setSubmitting(false);
    }
  }

  const count = counts?.count ?? 0;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- backdrop overlay; click outside fecha via target check
    <div
      className="fixed inset-0 z-[60] flex animate-fade-in items-center justify-center bg-background/80 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-label="Desvincular sensor"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md animate-scale-in">
        <CardContent className="space-y-3 pt-6">
          <h2 className="text-base font-semibold">Desvincular sensor</h2>
          <p className="text-xs text-muted-foreground">
            Sensor: <strong>{sensorLabel}</strong>
          </p>
          {!onUnlinkOverride && readingCountQuery.isError ? (
            // Não pode reafirmar "desvinculação é segura" com base em erro de
            // fetch; bloqueia a ação até confirmar (retry) quantas leituras existem.
            <Notify.QueryError
              variant="inline"
              error={readingCountQuery.error}
              onRetry={() => void readingCountQuery.refetch()}
              title="Não foi possível confirmar se há leituras recentes"
            />
          ) : (
            <Notify.Banner variant="warning">
              {onUnlinkOverride ? (
                <p>
                  Esta máquina ainda é um rascunho — sem leituras registradas. A desvinculação é
                  imediata.
                </p>
              ) : count > 0 ? (
                <p>
                  Este sensor possui <strong>{count}</strong> leitura(s) registrada(s) nos últimos 7
                  dias. A desvinculação <strong>não apaga</strong> o histórico — apenas impede
                  futuras leituras de alimentar este parâmetro.
                </p>
              ) : (
                <p>
                  Nenhuma leitura recente encontrada. A desvinculação é segura e reversível (basta
                  vincular novamente).
                </p>
              )}
            </Notify.Banner>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={
                submitting || unlink.isPending || (!onUnlinkOverride && readingCountQuery.isError)
              }
            >
              {submitting || unlink.isPending ? "Desvinculando…" : "Desvincular"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
